import {
  ANNUAL_DISCOUNT_PERCENT,
  CREDIT_PACKS,
  MOST_POPULAR_PLAN,
  PLAN_LIMITS,
  PLAN_NAMES,
  PLAN_SEATS,
  planPriceCents,
} from "@amtana/billing-contract";
import type { OfferedPlan, PlanId } from "@amtana/billing-contract";
import { WELCOME_CREDITS } from "@amtana/growth-contract";
import type { SupportedLanguage } from "@amtana/i18n";
import { PRODUCT_NAME } from "@amtana/i18n/catalog";

import { CREDIT_LIMITS, CREDIT_MEANING } from "../../../model/credit-words.ts";
import { cheapestWay, pricesFor } from "./cheapest-way.ts";
import { PRICING_WORDS_EN } from "./pricing-words-en.ts";
import { PRICING_WORDS_ES } from "./pricing-words-es.ts";
import { WEBSITE_PLANS } from "./website-plans.ts";

// ADR-034 point 2: what the website's pricing page states. Numbers come from the billing and growth
// contracts at build, words from this family, each formatted in the page's language, never the
// browser's, so the prerendered HTML and the first client render agree.

export type PricingPeriod = "month" | "year";

const WORDS = { en: PRICING_WORDS_EN, es: PRICING_WORDS_ES } as const;
const STARTING_PLAN: OfferedPlan = "starter";
const CHEAPEST_PACK = CREDIT_PACKS.analysis[0];

const fill = (text: string, values: Readonly<Record<string, string>>): string =>
  text.replace(/\{(\w+)\}/g, (place, name: string) => values[name] ?? place);

const hasCents = (cents: number): boolean => cents % 100 !== 0;

/** An amount in euros, as the page's language writes it: "€29" and "29 €". */
export function euros(cents: number, language: SupportedLanguage): string {
  const digits = hasCents(cents) ? 2 : 0;
  return new Intl.NumberFormat(language, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(cents / 100);
}

const whole = (value: number, language: SupportedLanguage): string =>
  new Intl.NumberFormat(language).format(value);

/** Every fact a pricing sentence can name, ready to fill a `{place}`. */
function factsIn(language: SupportedLanguage): Readonly<Record<string, string>> {
  return {
    product: PRODUCT_NAME,
    welcome: whole(WELCOME_CREDITS, language),
    discount: whole(ANNUAL_DISCOUNT_PERCENT, language),
    price: euros(planPriceCents(STARTING_PLAN, "month"), language),
    meaning: CREDIT_MEANING[language],
    limits: CREDIT_LIMITS[language],
  };
}

const say = (text: string, language: SupportedLanguage): string => fill(text, factsIn(language));

export const pricingKeyphrase = (language: SupportedLanguage): string => WORDS[language].keyphrase;
export const pricingTitle = (language: SupportedLanguage): string => say(WORDS[language].title, language);
export const pricingDescription = (language: SupportedLanguage): string =>
  say(WORDS[language].description, language);

/** The visible FAQ and its `FAQPage` block read this one list (ADR-034 point 6). */
export function pricingQuestions(language: SupportedLanguage) {
  return WORDS[language].questions.map((entry) => ({
    question: entry.question,
    answer: say(entry.answer, language),
  }));
}

const planName = (plan: PlanId, language: SupportedLanguage): string =>
  plan === "free" ? WORDS[language].plans.free.name : PLAN_NAMES[plan];

/** A plan on sale always states its credits and seats; only Unlimited, off sale, has neither cap. */
const creditsOf = (plan: OfferedPlan): number => PLAN_LIMITS[plan].credits ?? 0;

/** One `Offer` per plan, free first, each at the monthly price its card opens on. */
export function pricingOffers(language: SupportedLanguage) {
  return [
    { name: planName("free", language), priceCents: 0 },
    ...WEBSITE_PLANS.map((plan) => ({
      name: planName(plan, language),
      priceCents: planPriceCents(plan, "month"),
    })),
  ];
}

export type PlanCard = {
  readonly id: PlanId;
  readonly name: string;
  readonly tagline: string;
  readonly price: string;
  readonly wasPrice: string | undefined;
  readonly period: string;
  readonly note: string | undefined;
  /** The badge on the plan most people pick; the billing contract names which (D1329). */
  readonly ribbon: string | undefined;
  readonly lines: readonly string[];
  readonly action: string;
  /** What a click on the card's action reports (ADR-027 point 9). */
  readonly cta: `pricing-${PlanId}`;
};

function paidCard(plan: OfferedPlan, period: PricingPeriod, language: SupportedLanguage): PlanCard {
  const words = WORDS[language];
  const monthly = planPriceCents(plan, "month");
  return {
    id: plan,
    name: planName(plan, language),
    tagline: words.plans[plan].tagline,
    price: euros(planPriceCents(plan, period), language),
    wasPrice: period === "year" ? euros(monthly * 12, language) : undefined,
    period: period === "year" ? words.perYear : words.perMonth,
    note: say(period === "year" ? words.billedYearly : words.billedMonthly, language),
    ribbon: plan === MOST_POPULAR_PLAN ? words.mostPopular : undefined,
    lines: [
      fill(words.creditsLine, { credits: whole(creditsOf(plan), language) }),
      fill(words.seatsLine, { seats: whole(PLAN_SEATS[plan].included ?? 0, language) }),
    ],
    action: words.plans[plan].action,
    cta: `pricing-${plan}`,
  };
}

/** The cards in order: the free plan first, since it has no credits of its own, then each paid plan. */
export function planCards(period: PricingPeriod, language: SupportedLanguage): readonly PlanCard[] {
  const free = WORDS[language].plans.free;
  const facts = { ...factsIn(language), seats: whole(PLAN_SEATS.free.included ?? 0, language) };
  return [
    {
      id: "free",
      name: free.name,
      tagline: free.tagline,
      price: euros(0, language),
      wasPrice: undefined,
      period: "",
      note: undefined,
      ribbon: undefined,
      lines: free.lines.map((line) => fill(line, facts)),
      action: free.action,
      cta: "pricing-free",
    },
    ...WEBSITE_PLANS.map((plan) => paidCard(plan, period, language)),
  ];
}

/** The cheapest plan the website shows whose credits cover `credits`; undefined above the largest. */
export function planThatCovers(credits: number): OfferedPlan | undefined {
  return WEBSITE_PLANS.find((plan) => creditsOf(plan) >= credits);
}

const LARGEST_PLAN: OfferedPlan = WEBSITE_PLANS.at(-1) ?? STARTING_PLAN;

/** The credits someone typed, up to a whole number; undefined when that is not a positive number. */
function typedCredits(typed: string): number | undefined {
  const credits = Math.ceil(Number(typed));
  return typed.trim() !== "" && Number.isSafeInteger(credits) && credits > 0 ? credits : undefined;
}

/** The helper's answer to what someone typed: the cheapest true way to reach it (D1339), or its
 * prompt. A pack is paid once, so its price joins a plan's only as "the first month" or "up front". */
export function coveringLine(typed: string, period: PricingPeriod, language: SupportedLanguage): string {
  const words = WORDS[language];
  const credits = typedCredits(typed);
  if (credits === undefined) return words.helperEmpty;
  const way = cheapestWay(credits, pricesFor(period));
  const amount = whole(credits, language);
  if (way.kind === "welcome") {
    return fill(words.helperWelcome, {
      plan: planName("free", language),
      welcome: whole(WELCOME_CREDITS, language),
      credits: amount,
    });
  }
  const one = way.packs === 1;
  const pack = euros(way.packCents, language);
  const count = whole(way.packs, language);
  if (!way.plan) {
    return fill(one ? words.helperPackAlone : words.helperPacksAlone, { pack, count, credits: amount });
  }
  const price = planPriceCents(way.plan, period);
  const facts = {
    plan: planName(way.plan, language),
    credits: amount,
    price: euros(price, language),
    pack,
    count,
    total: euros(price + way.packCents, language),
  };
  if (way.packs === 0) return fill(period === "year" ? words.helperYear : words.helperMonth, facts);
  if (period === "year") return fill(one ? words.helperPlanPackYear : words.helperPlanPacksYear, facts);
  return fill(one ? words.helperPlanPackMonth : words.helperPlanPacksMonth, facts);
}

/** The smallest credit pack, stated from the contract. */
export function packLine(language: SupportedLanguage): string {
  const pack = CHEAPEST_PACK;
  if (!pack) return "";
  return fill(WORDS[language].helperPack, {
    price: euros(pack.cents, language),
    credits: whole(pack.credits, language),
  });
}

/** What the helper adds under its answer: that packs do not renew, and the plan that would include
 * the credits when the cheapest way is not that plan. */
export function helperNote(typed: string, period: PricingPeriod, language: SupportedLanguage): string {
  const words = WORDS[language];
  const credits = typedCredits(typed);
  const way = credits === undefined ? undefined : cheapestWay(credits, pricesFor(period));
  if (credits === undefined || way?.kind !== "paid" || way.packs === 0) return packLine(language);
  const once = fill(words.helperPacksOnce, { credits: whole(credits, language) });
  const including = planThatCovers(credits) ?? LARGEST_PLAN;
  if (including === way.plan) return once;
  const also = fill(period === "year" ? words.helperAlsoYear : words.helperAlsoMonth, {
    plan: planName(including, language),
    included: whole(creditsOf(including), language),
    price: euros(planPriceCents(including, period), language),
  });
  return `${once} ${also}`;
}

/** What the screen shows beyond the cards, in `language`. */
export function pricingWords(language: SupportedLanguage) {
  const words = WORDS[language];
  return {
    ...words,
    heading: say(words.heading, language),
    lede: say(words.lede, language),
    periodDiscount: say(words.periodDiscount, language),
  };
}
